import React, { useState, useEffect, useRef, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Slider from '@mui/material/Slider'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import MuiInput from '@mui/material/Input'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DownloadIcon from '@mui/icons-material/Download'

import { useAuth0 } from "@auth0/auth0-react"
import { debounce } from 'lodash'
import { ResponsiveContainer, ComposedChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import {StlViewer} from 'react-stl-viewer'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import calculateDrag from '../lib/mr-bullet.js'
import drawPrint from '../lib/mr-bullet-print.js'
import createBulletSTL from '../lib/stl.js'
import gcode from '../lib/gcode.js'

const drivingBandSizes = {
  1: .004, // .177
  2: .005, // .204
  3: .005, // .224
  4: .007, // .243
  5: .007, // .257
  6: .008, // .264
  7: .007, // .277
  8: .007, // .284
  9: .008, // .308
  10: .008, // .338
  11: .007, // .357
  12: .009, // .375
  13: .008, // .408
  14: .008, // .416
  15: .008, // .458
  16: .010, // .510
  //17: .020 // 1
}

const calibers = {
  1: .177,
  2: .204,
  3: .224,
  4: .243,
  5: .257,
  6: .264,
  7: .277,
  8: .284,
  9: .308,
  10: .338,
  11: .357,
  12: .375,
  13: .408,
  14: .416,
  15: .458,
  16: .510,
  //17: 1
}

function BulletDesigner() {
  const { user } = useAuth0()
  const isAdmin = user && user.sub === "google-oauth2|104861804208992956729"
  const printRef = useRef(null)
  const [diameter, setDiameter] = useAutoSaveState('diameter', .308)
  const [noseType, setNoseType] = useAutoSaveState('noseType', 'secant')
  const [material, setMaterial] = useAutoSaveState('material', 'copper')
  const [bearingSurfaceLength, setBearingSurfaceLength] = useAutoSaveState('bearingSurfaceLength', .5)
  const [noseLength, setNoseLength] = useAutoSaveState('noseLength', .9)
  const [velocity, setVelocity] = useAutoSaveState('velocity', 2800)
  const [baseWeight, setBaseWeight] = useAutoSaveState('baseWeight', 0)
  const [useBaseWeight, setUseBaseWeight] = useAutoSaveState('useBaseWeight', 0)
  const [boattailLength, setBoattailLength] = useAutoSaveState('boattailLength', .225)
  const [baseDiameter, setBaseDiameter] = useAutoSaveState('baseDiameter', .260)
  const [drivingBands, setDrivingBands] = useAutoSaveState('drivingBands', 0)
  const [meplat, _setMeplat] = useAutoSaveState('meplat', .04)
  const setMeplat = meplat => {
    //console.trace('setMeplat', meplat)
    _setMeplat(meplat)
  }
  const [rtr, setRtr] = useAutoSaveState('rtr', .5)
  const [baseCavityLength, setBaseCavityLength] = useAutoSaveState('baseCavityLength', 0)
  const [tipLength, setTipLength] = useAutoSaveState('tipLength', 0)
  const [tipStemDiameter, setTipStemDiameter] = useAutoSaveState('tipStemDiameter', 0)
  const [tipStemLength, setTipStemLength] = useAutoSaveState('tipStemLength', 0)
  const [tipCoreLength, setTipCoreLength] = useAutoSaveState('tipCoreLength', 0)
  const [baseCavityDiameter, setBaseCavityDiameter] = useAutoSaveState('baseCavityDiameter', 0)
  const [noseCavity, setNoseCavity] = useAutoSaveState('noseCavity', 0)
  const [jacketThickness, setJacketThickness] = useAutoSaveState('jacketThickness', 0)
  const [targetRange, setTargetRange] = useAutoSaveState('targetRange', 1000)
  const [caliberId, setCaliberId] = useAutoSaveState('caliberId', 9)
  const [stlUrl, setStlUrl] = useState(null)
  const [dragChartData, setDragChartData] = useState([])
  const [bcChartData, setBcChartData] = useState([])
  const [sdChartData, setSdChartData] = useState([])
  const [coeffChartData, setCoeffChartData] = useState([])
  const [mcdrag, setMcdrag] = useState(null)
  const [power, setPower] = useAutoSaveState('power', .75)
  const [haackC, setHaackC] = useAutoSaveState('haackC', 0)
  const [searching, setSearching] = useState(false)
  const [searchIterations, setSearchIterations] = useAutoSaveState('searchIterations', '1000')
  const [iterations, setIterations] = useState(0)
  const [ngc, setNgc] = useState('')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [searchParams, setSearchParams] = useAutoSaveState('searchParams', {
    caliberId: { min: 9, max: 9 },
    noseLength: { min: .5 , max: 3 },
    noseCavity: { min: .5 , max: 2 },
    jacketThickness: { min: .07, max: .07 },
    bearingSurfaceLength: { min: 1, max: 2 },
    boattailLength: { min: 0, max: 1 },
    baseDiameter: { min: .6, max: 1 },
    baseCavityDiameter: isAdmin ? { min: 0, max: 1 } : { min: 0, max: 0 },
    baseCavityLength: isAdmin ? { min: 0, max: 3 } : { min: 0, max: 0 },
    tipLength: isAdmin ? { min: 0, max: 3 } : { min: 0, max: 0 },
    tipStemDiameter: isAdmin ? { min: 0, max: .8 } : { min: 0, max: 0 },
    tipStemLength: isAdmin ? { min: 0, max: 1.5 } : { min: 0, max: 0 },
    tipCoreLength: isAdmin ? { min: 0, max: 0 } : { min: 0, max: 0 },
    meplat: { min: 0, max: .3 },
    drivingBands: { min: 0, max: 0 },
    rtr: { min: .3, max: 1 },
    power: { min: .5, max: .99 },
    haackC: { min: 0, max: 0 },
    noseTypes: { haack: false, power: false, secant: true },
    boattailAngle: { min: 0, max: 10 },
    twist: { min: 8, max: 20 },
    weight: { min: 0, max: 7000 },
    velocityConstraint: { min: 0, max: 6000 },
    optimize: 'superFF',
    targetRange: '1000',
    velocity: '2800',
    useBaseWeight: false
  })

  useEffect(() => {
    if (material === 'jacket') {
      if (jacketThickness === 0) setJacketThickness(diameter * .07)
      if (noseCavity === 0) setNoseCavity(diameter * .3)
      setBaseCavityDiameter(0)
      setBaseCavityLength(0)
      setTipStemLength(0)
      setTipStemDiameter(0)
      setTipLength(0)
      if (noseType !== 'secant') {
        setNoseType('secant')
      }
    }
    if (material !== 'jacket') {
      setJacketThickness(0)
      setNoseCavity(0)
    }
  }, [material])

  const setSearchParam = (key, value) => {
    setSearchParams({ ...searchParams, [key]: value })
  }
  useEffect(() => {
    console.log('searching', searching)
    if (!searching) return
    search()
  }, [searching])

  const search = async () => {
    let best
    let bestParams
    let compare
    if (searchParams.optimize.includes('BC') || searchParams.optimize.includes('penetration')) {
      best = Math.max()
      compare = (a, b) => a > b
    } else {
      best = Math.min()
      compare = (a, b) => a < b
    }
    for (let i = 0; i < searchIterations; i++) {
      if (!searching) return
      const caliberId = Math.floor(Math.random() * (searchParams.caliberId.max - searchParams.caliberId.min) + searchParams.caliberId.min)
      const diameter = calibers[caliberId]
      let noseLength = diameter * (Math.random() * (searchParams.noseLength.max - searchParams.noseLength.min) + searchParams.noseLength.min)
      let bearingSurfaceLength = diameter * (Math.random() * (searchParams.bearingSurfaceLength.max - searchParams.bearingSurfaceLength.min) + searchParams.bearingSurfaceLength.min)
      let noseCavity = diameter * (Math.random() * (searchParams.noseCavity.max - searchParams.noseCavity.min) + searchParams.noseCavity.min)
      let boattailLength = diameter * (Math.random() * (searchParams.boattailLength.max - searchParams.boattailLength.min) + searchParams.boattailLength.min)
      let baseDiameter = diameter * (Math.random() * (searchParams.baseDiameter.max - searchParams.baseDiameter.min) + searchParams.baseDiameter.min)
      let meplat = diameter * (Math.random() * (searchParams.meplat.max - searchParams.meplat.min) + searchParams.meplat.min)
      let rtr = Math.random() * (searchParams.rtr.max - searchParams.rtr.min) + searchParams.rtr.min
      let power = Math.random() * (searchParams.power.max - searchParams.power.min) + searchParams.power.min
      let haackC = Math.random() * (searchParams.haackC.max - searchParams.haackC.min) + searchParams.haackC.min
      let noseTypes = Object.keys(searchParams.noseTypes).filter(k => searchParams.noseTypes[k])
      let noseType = noseTypes[Math.floor(Math.random() * noseTypes.length)]
      let drivingBands = Math.floor(Math.random() * (searchParams.drivingBands.max - searchParams.drivingBands.min)) + searchParams.drivingBands.min
      let jacketThickness = diameter * (Math.random() * (searchParams.jacketThickness.max - searchParams.jacketThickness.min) + searchParams.jacketThickness.min)
      let baseCavityDiameter = diameter * (Math.random() * (searchParams.baseCavityDiameter.max - searchParams.baseCavityDiameter.min) + searchParams.baseCavityDiameter.min)
      let baseCavityLength = diameter * (Math.random() * (searchParams.baseCavityLength.max - searchParams.baseCavityLength.min) + searchParams.baseCavityLength.min)
      let tipLength = diameter * (Math.random() * (searchParams.tipLength.max - searchParams.tipLength.min) + searchParams.tipLength.min)
      let tipStemDiameter = diameter * (Math.random() * (searchParams.tipStemDiameter.max - searchParams.tipStemDiameter.min) + searchParams.tipStemDiameter.min)
      let tipStemLength = diameter * (Math.random() * (searchParams.tipStemLength.max - searchParams.tipStemLength.min) + searchParams.tipStemLength.min)
      let tipCoreLength = diameter * (Math.random() * (searchParams.tipCoreLength.max - searchParams.tipCoreLength.min) + searchParams.tipCoreLength.min)
      let grains = null
      if (bestParams && Math.random() > .5) {
        if (Math.random() > .5) noseLength = bestParams.noseLength
        if (Math.random() > .5) jacketThickness = bestParams.jacketThickness
        if (Math.random() > .5) bearingSurfaceLength = bestParams.bearingSurfaceLength
        if (Math.random() > .5) noseCavity = bestParams.noseCavity
        if (Math.random() > .5) boattailLength = bestParams.boattailLength
        if (Math.random() > .5) baseDiameter = bestParams.baseDiameter
        if (Math.random() > .5) meplat = bestParams.meplat
        if (Math.random() > .5) drivingBands = bestParams.drivingBands
        if (Math.random() > .5) rtr = bestParams.rtr
        if (Math.random() > .5) power = bestParams.power
        if (Math.random() > .5) haackC = bestParams.haackC
        if (Math.random() > .5) noseType = bestParams.noseType
        if (Math.random() > .5) baseCavityDiameter = bestParams.baseCavityDiameter
        if (Math.random() > .5) baseCavityLength = bestParams.baseCavityLength
        if (Math.random() > .5) tipLength = bestParams.tipLength
        if (Math.random() > .5) tipStemDiameter = bestParams.tipStemDiameter
        if (Math.random() > .5) tipStemLength = bestParams.tipStemLength
        if (Math.random() > .5) tipCoreLength = bestParams.tipCoreLength
      }
      if (bestParams && Math.random() > .9) {
        if (Math.random() > .9) noseLength = bestParams.noseLength
        if (Math.random() > .9) jacketThickness = bestParams.jacketThickness
        if (Math.random() > .9) bearingSurfaceLength = bestParams.bearingSurfaceLength
        if (Math.random() > .9) noseCavity = bestParams.noseCavity
        if (Math.random() > .9) boattailLength = bestParams.boattailLength
        if (Math.random() > .9) baseDiameter = bestParams.baseDiameter
        if (Math.random() > .9) meplat = bestParams.meplat
        if (Math.random() > .9) drivingBands = bestParams.drivingBands
        if (Math.random() > .9) rtr = bestParams.rtr
        if (Math.random() > .9) power = bestParams.power
        if (Math.random() > .9) haackC = bestParams.haackC
        if (Math.random() > .9) noseType = bestParams.noseType
        if (Math.random() > .9) baseCavityDiameter = bestParams.baseCavityDiameter
        if (Math.random() > .9) baseCavityLength = bestParams.baseCavityLength
        if (Math.random() > .9) tipLength = bestParams.tipLength
        if (Math.random() > .9) tipStemDiameter = bestParams.tipStemDiameter
        if (Math.random() > .9) tipStemLength = bestParams.tipStemLength
        if (Math.random() > .9) tipCoreLength = bestParams.tipCoreLength
      }
      if (material !== 'jacket') {
        jacketThickness = 0
        noseCavity = 0
      } else {
        noseCavity = Math.min(noseLength, noseCavity)
        noseType = 'secant'
        drivingBands = 0
        baseCavityDiameter = 0
        baseCavityLength = 0
        tipLength = 0
        tipStemDiameter = 0
        tipStemLength = 0
        tipCoreLength = 0
      }
      if (bestParams && Math.random() > .8) {
        noseLength = bestParams.noseLength
        jacketThickness = bestParams.jacketThickness
        bearingSurfaceLength = bestParams.bearingSurfaceLength
        noseCavity = bestParams.noseCavity
        boattailLength = bestParams.boattailLength
        baseDiameter = bestParams.baseDiameter
        meplat = bestParams.meplat
        drivingBands = bestParams.drivingBands
        rtr = bestParams.rtr
        power = bestParams.power
        haackC = bestParams.haackC
        noseType = bestParams.noseType
        baseCavityDiameter = bestParams.baseCavityDiameter
        baseCavityLength = bestParams.baseCavityLength
        tipLength = bestParams.tipLength
        tipStemDiameter = bestParams.tipStemDiameter
        tipStemLength = bestParams.tipStemLength
        tipCoreLength = bestParams.tipCoreLength
        const keys = [
          'noseLength', 'jacketThickness', 'bearingSurfaceLength', 'noseCavity', 'boattailLength',
          'baseDiameter', 'meplat', 'drivingBands', 'rtr', 'power', 'haackC',
          'baseCavityDiameter', 'baseCavityLength', 'tipLength', 'tipStemDiameter', 'tipStemLength', 'tipCoreLength'
        ]
        const keyToChange = 'tipCoreLength' // keys[Math.floor(Math.random() * keys.length)]
        let val = bestParams[keyToChange]
        let change = Math.random()
        if (change > .6666) {
          val += (Math.random() > .5 ? .001 : -.001)
        } else if (change > .3333) {
          val += (Math.random() > .5 ? val *.01 : val * -.01)
        } else {
          val = diameter * (Math.random() * (searchParams[keyToChange].max - searchParams[keyToChange].min) + searchParams[keyToChange].min)
        }
        if (val >= searchParams[keyToChange].min && val <= searchParams[keyToChange].max) {
          bestParams[keyToChange] = val
        } else {
          bestParams[keyToChange] = diameter * (Math.random() * (searchParams[keyToChange].max - searchParams[keyToChange].min) + searchParams[keyToChange].min)
        }
      }
      if (tipLength + tipStemLength + tipCoreLength + baseCavityLength + .5 > noseLength + bearingSurfaceLength + boattailLength) {
        continue
      }
      if (baseCavityDiameter > (baseDiameter - .04)) {
        baseCavityDiameter = baseDiameter - .04
      }
      const drivingBandDiameter = drivingBands > 0
        ? drivingBandSizes[caliberId]
        : 0
      const hybridRadius = 0
      const grainsPerCubicIn = {
        lead: 2850,
        copper: 2261,
        brass: 1985
      }[material]
      const altitude = 0
      const sg = 1.5
      const mcdrag = calculateDrag({
        grains,
        diameter: diameter - drivingBandDiameter,
        bearingSurfaceLength,
        jacketThickness,
        noseLength,
        noseCavity,
        noseType,
        rtr,
        baseCavityDiameter,
        baseCavityLength,
        tipLength,
        tipStemDiameter,
        tipStemLength,
        tipCoreLength,
        power,
        haackC,
        boattailLength,
        baseDiameter,
        meplat,
        drivingBandDiameter,
        drivingBands,
        hybridRadius,
        grainsPerCubicIn,
        altitude,
        velocity: parseFloat(searchParams.velocity),
        baseWeight: searchParams.useBaseWeight ? parseFloat(searchParams.baseWeight) : 0,
        sg,
        targetRange: parseFloat(searchParams.targetRange),
        //M: [ 2.5 ],
        constraints: {
          boattailAngle: searchParams.boattailAngle,
          twist: searchParams.twist,
          velocity: searchParams.velocityConstraint,
          weight: searchParams.weight
        }
      })
      if (i % 100 === 0) await new Promise(resolve => setTimeout(resolve, 1))
      if (
        mcdrag &&
        (
          compare(mcdrag[searchParams.optimize], best) ||
          (searchParams.optimize === 'superFF' && mcdrag[searchParams.optimize] === best && mcdrag.twistRate > bestParams.twistRate)
        )
      ) {
        console.log('best', best)
        best = mcdrag[searchParams.optimize]
        bestParams = {
          twistRate: mcdrag.twistRate,
          caliberId,
          diameter,
          noseLength,
          jacketThickness,
          bearingSurfaceLength,
          noseCavity,
          baseCavityDiameter,
          baseCavityLength,
          tipLength,
          tipStemDiameter,
          tipStemLength,
          tipCoreLength,
          boattailLength,
          baseDiameter,
          meplat,
          drivingBands,
          rtr,
          power,
          haackC,
          noseType
        }
        setCaliberId(caliberId)
        setDiameter(diameter)
        setVelocity(parseFloat(searchParams.velocity))
        setUseBaseWeight(searchParams.useBaseWeight)
        if (searchParams.useBaseWeight) {
          setBaseWeight(parseFloat(searchParams.baseWeight))
        }
        setTargetRange(parseFloat(searchParams.targetRange))
        setNoseLength(noseLength)
        setJacketThickness(jacketThickness)
        setNoseCavity(noseCavity)
        setBearingSurfaceLength(bearingSurfaceLength)
        setBoattailLength(boattailLength)
        setBaseDiameter(baseDiameter)
        setMeplat(meplat)
        setDrivingBands(drivingBands)
        setRtr(rtr)
        setBaseCavityDiameter(baseCavityDiameter)
        setBaseCavityLength(baseCavityLength)
        setTipLength(tipLength)
        setTipStemDiameter(tipStemDiameter)
        setTipStemLength(tipStemLength)
        setTipCoreLength(tipCoreLength)
        setPower(power)
        setHaackC(haackC)
        setNoseType(noseType)
        setIterations(i)
        await new Promise(resolve => setTimeout(resolve, 15))
      }
      if (i % 100 === 0) setIterations(i)
    }
    setSearching(false)
  }

  useEffect(() => {
    const oldDiameter = diameter
    const newDiameter = calibers[caliberId]
    setDiameter(newDiameter)
    setBearingSurfaceLength(bearingSurfaceLength => parseFloat((bearingSurfaceLength * newDiameter / oldDiameter).toFixed(3)))
    setNoseLength(noseLength => noseLength * parseFloat((newDiameter / oldDiameter).toFixed(3)))
    setJacketThickness(jacketThickness => jacketThickness * parseFloat((newDiameter / oldDiameter).toFixed(3)))
    setNoseCavity(noseCavity => noseCavity * parseFloat((newDiameter / oldDiameter).toFixed(3)))
    setBoattailLength(boattailLength => parseFloat((boattailLength * newDiameter / oldDiameter).toFixed(3)))
    setBaseDiameter(baseDiameter => parseFloat((baseDiameter * newDiameter / oldDiameter).toFixed(3)))
    setMeplat(meplat => meplat * parseFloat((newDiameter / oldDiameter).toFixed(3)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caliberId])
  useEffect(() => {
    const grains = null
    const drivingBandDiameter = drivingBands > 0
        ? drivingBandSizes[caliberId]
        : 0
    const hybridRadius = 0
    const grainsPerCubicIn = {
      lead: 2850,
      copper: 2261,
      brass: 1985
    }[material]
    const altitude = 0
    const sg = 1.5
    const mcdrag = calculateDrag({
      grains,
      diameter: diameter - drivingBandDiameter,
      bearingSurfaceLength,
      jacketThickness,
      noseLength,
      noseCavity,
      noseType,
      rtr,
      baseCavityDiameter,
      baseCavityLength,
      tipLength,
      tipStemDiameter,
      tipStemLength,
      tipCoreLength,
      power,
      haackC,
      boattailLength,
      baseDiameter,
      meplat,
      drivingBandDiameter,
      drivingBands,
      hybridRadius,
      grainsPerCubicIn,
      altitude,
      velocity,
      targetRange,
      baseWeight: useBaseWeight ? baseWeight : 0,
      sg
    })
    setMcdrag(mcdrag)
    setPreview(`Weight: ${mcdrag.grains.toFixed(0)}gr, G7: ${mcdrag.superBC.toFixed(3)} FF: ${mcdrag.superFF.toFixed(3)}`)
    drawPrint(mcdrag, printRef.current)
    const ngc = gcode({
      bullet: mcdrag,
      noseType,
      stockDiameter: .437,
      offset: .1,
      cutoffToolWidth: .138,
      cutoffOffsetX: 1.586,
      cutoffOffsetZ: .012,
      backlash: 0.0045,
      faceDoc: .01,
      faceFeedrate: 1,
      roughDoc: .04,
      roughFeedrate: 4,
      finishDoc1: .01,
      finishFeedrate1: 4,
      finishDoc2: .01,
      finishFeedrate2: 2,
      cutoffFeedrate: .5,
    })
    setNgc(ngc)
    if (mcdrag && mcdrag.M && mcdrag.M.map) {
      const dragChartData = mcdrag.M.map((M, index) => ({
        Mach: M,
        G7: parseFloat(mcdrag.G7[index].toFixed(3)),
        Cd: parseFloat(mcdrag.C1[index].toFixed(3)),
        Nose: parseFloat(mcdrag.C2[index].toFixed(3)),
        Surface: parseFloat(mcdrag.C3[index].toFixed(3)),
        DrivingBand: parseFloat(mcdrag.C4[index].toFixed(3)),
        Boattail: parseFloat(mcdrag.C5[index].toFixed(3)),
        Base: parseFloat(mcdrag.C6[index].toFixed(3)),
      }));
      setDragChartData(dragChartData)

      const bcChartData = mcdrag.M.map((M, index) => ({
        Mach: M,
        BC: parseFloat(mcdrag.BC[index].toFixed(3)),
        FF: parseFloat(mcdrag.FF[index].toFixed(3)),
      }));
      setBcChartData(bcChartData)

      const sdChartData = mcdrag.M.map((M, index) => {
        let Sd = parseFloat(mcdrag.coeffValues.Sd[index].toFixed(3))
        if (Sd > 3 || Sd < -3) Sd = undefined
        return {
          Mach: M,
          Sd,
          stable: parseFloat(mcdrag.coeffValues.stable[index].toFixed(3)),
        }
      })
      setSdChartData(sdChartData)

      try {
        const coeffChartData = mcdrag.M.map((M, index) => ({
          Mach: M,
          Cnpa : parseFloat(mcdrag.coeffValues.Cnpa[index].toFixed(3)),
          Cla: parseFloat(mcdrag.coeffValues.Cla[index].toFixed(3)),
          Clp: parseFloat(mcdrag.coeffValues.Clp[index].toFixed(3)),
          Cma: parseFloat(mcdrag.coeffValues.Cma[index].toFixed(3)),
          Cda2: parseFloat(mcdrag.coeffValues.Cda2[index].toFixed(3)),
          Cna: parseFloat(mcdrag.coeffValues.Cna[index].toFixed(3)),
          Cmq: parseFloat(mcdrag.coeffValues.Cmq[index].toFixed(3)),
        }));
        setCoeffChartData(coeffChartData)
        const sixDofExport = `{
  Ix: ${mcdrag.Ix.toFixed(3)},
  Iy: ${mcdrag.Iy.toFixed(3)},
  mass: ${mcdrag.grains.toFixed(1)},
  diameter: ${(mcdrag.diameter).toFixed(3)},
  twistRate: ${mcdrag.twistRate.toFixed(1)},
  v0: ${velocity},
  Cd: ${JSON.stringify(mcdrag.coeffValues.Cd.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  Cda2: ${JSON.stringify(mcdrag.coeffValues.Cda2.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  Cla: ${JSON.stringify(mcdrag.coeffValues.Cla.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  Cma: ${JSON.stringify(mcdrag.coeffValues.Cma.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  Clp: ${JSON.stringify(mcdrag.coeffValues.Clp.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  CmqCma: ${JSON.stringify(mcdrag.coeffValues.Cmq.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  Cnpa: ${JSON.stringify(mcdrag.coeffValues.Cnpa.map((v, i) => [mcdrag.M[i], parseFloat(v.toFixed(3))]))},
  Cla2: [[0,0]],
  Cnpa2: [[0,0]],
  Cma2: [[0,0]],
  CmqCma2: [[0,0]],
  Cypa: [[0,0]],
  Cypa2: [[0,0]],
  Cnq: [[0,0]],
  Cnq2: [[0,0]]
}`
        console.log(sixDofExport)
      } catch (e) {
        setCoeffChartData([])
      }
    }
  }, [
    caliberId,
    diameter,
    bearingSurfaceLength,
    jacketThickness,
    velocity,
    baseWeight,
    useBaseWeight,
    targetRange,
    noseLength,
    noseCavity,
    rtr,
    baseCavityDiameter,
    baseCavityLength,
    tipLength,
    tipStemDiameter,
    tipStemLength,
    tipCoreLength,
    power,
    haackC,
    boattailLength,
    baseDiameter,
    meplat,
    drivingBands,
    material,
    noseType
  ])
  useLazyEffect(() => {
    if (!mcdrag) return
    const CG = mcdrag.CG
    const drivingBandDiameter = drivingBands > 0
        ? drivingBandSizes[caliberId]
        : 0
    let stlDiameters = [
      { diameter: 0, dist: -CG },
      { diameter: baseDiameter, dist: -CG },
    ]
    if (drivingBands > 0) {
      stlDiameters = [
        ...stlDiameters,
        { diameter: diameter - drivingBandDiameter, dist: boattailLength - CG },
      ]
      for (let i = 0; i < drivingBands; i++) {
        const len = bearingSurfaceLength / drivingBands
        stlDiameters = [
          ...stlDiameters,
          { diameter: diameter,                       dist: (len * i) + boattailLength - CG },
          { diameter: diameter,                       dist: (len * (i + .5)) + boattailLength - CG },
          { diameter: diameter - drivingBandDiameter, dist: (len * (i + .5)) + boattailLength - CG },
          { diameter: diameter - drivingBandDiameter, dist: (len * (i + 1)) + boattailLength - CG },
        ]
      }
    } else {
      const rebate = 0
      stlDiameters = [
        ...stlDiameters,
        { diameter: diameter - rebate, dist: boattailLength - CG },
        { diameter: diameter, dist: boattailLength - CG },
        { diameter: diameter, dist: bearingSurfaceLength + boattailLength - CG },
      ]
    }
    stlDiameters = [
      ...stlDiameters,
      ...mcdrag.nosePoints.map(p => ({
        diameter: p[1] * 2,
        dist: p[0] + boattailLength + bearingSurfaceLength - CG
      })),
    ]
    if (noseType === 'secant') {
      stlDiameters = [
        ...stlDiameters,
        { diameter: meplat, dist: noseLength + bearingSurfaceLength + boattailLength - CG },
      ]
    }
    stlDiameters = [
      ...stlDiameters,
      { diameter: 0, dist: noseLength + bearingSurfaceLength + boattailLength - CG },
    ]
    const invalid = stlDiameters.some(p => isNaN(p.diameter) || isNaN(p.dist))
    if (invalid) return
    const stl = createBulletSTL(stlDiameters)
    const blob = new Blob([stl], { type: 'model/stl' })
    const url = URL.createObjectURL(blob)
    setStlUrl(url)
  }, [ mcdrag ], 1000)

  const downloadStl = () => {
    const a = document.createElement('a')
    a.href = stlUrl
    a.download = 'bullet.stl'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const [stickyTop, setStickyTop] = useState(0);

  const checkSticky = () => {
    const topPosition = document.getElementById('stickyElement').getBoundingClientRect().top;
    setStickyTop(-topPosition);
  }

  useEffect(() => {
    window.addEventListener('scroll', checkSticky);
    return () => {
      window.removeEventListener('scroll', checkSticky);
    }
  }, [])

  return (
    <Container sx={{ mt: 4, mb: 4 }} maxWidth={false}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" gutterBottom style={{ textAlign: 'center' }}>
            <img src="/mr-bullet2.png" style={{ height: 60, position: 'relative', top: 10, paddingRight: 20 }} alt="" />
            Mr. Bullet
            <SaveButton />
          </Typography>
        </Grid>
        <NameField />
        <Grid item xs={12} sm={3} id="stickyElement">
          <Paper sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            //maxHeight: 'calc(100vh - 100px)',
            //overflowY: 'auto',
            //...(stickyTop > 0 && { position: 'relative', top: stickyTop })
          }}>
            <Box mb={2} mt={1}>
              <FormControl fullWidth>
                <InputLabel>Material</InputLabel>
                <Select
                  fullWidth
                  value={material}
                  label="Material"
                  onChange={e => setMaterial(e.target.value)}
                >
                  <MenuItem value="jacket">Copper Jacket & Lead Core</MenuItem>
                  <MenuItem value="copper">Copper</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {material !== 'jacket' && <Box mb={2}>
              <FormControl fullWidth>
                <InputLabel>Nose Type</InputLabel>
                <Select
                  fullWidth
                  label="Nose Type"
                  value={noseType}
                  onChange={e => setNoseType(e.target.value)}
                >
                  <MenuItem value="secant">Secant</MenuItem>
                  <MenuItem value="haack">Haack</MenuItem>
                  <MenuItem value="power">Power</MenuItem>
                </Select>
              </FormControl>
            </Box>}
            <Box mb={2}>
              <Typography id="input-slider" gutterBottom>
                Caliber
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <Slider
                    value={caliberId}
                    onChange={e => setCaliberId(e.target.value)}
                    step={1} min={1} max={16}
                  />
                </Grid>
                <Grid item sx={{ width: '90px' }}>
                  {diameter}
                </Grid>
              </Grid>
            </Box>
            <FormGroup>
              <FormControlLabel control={
                <Checkbox checked={useBaseWeight} onChange={e => setUseBaseWeight(!useBaseWeight)} />
              } label="Adjust velocity for weight"  />
            </FormGroup>
            {useBaseWeight
              ? <>
                <InputSlider label="Known Velocity" value={velocity} setValue={setVelocity} step={1} min={1000} max={3700} />
                <InputSlider label="Known Weight" value={baseWeight} setValue={setBaseWeight} step={1} min={0} max={1000} />
              </>
              : <InputSlider label="Velocity" value={velocity} setValue={setVelocity} step={1} min={1000} max={3700} />
            }
            <InputSlider label="Nose Length" value={noseLength} setValue={setNoseLength} step={.001} min={diameter / 2} max={diameter * 5} />
            {material === 'jacket' && <InputSlider label="Nose Cavity" value={noseCavity} setValue={setNoseCavity} step={.001} min={0} max={diameter * 5} />}
            {material === 'jacket' && <InputSlider label="Jacket Thickness" value={jacketThickness} setValue={setJacketThickness} step={.001} min={0} max={diameter * .3} />}
            {noseType === 'secant' && <InputSlider label="Rt/R" value={rtr} setValue={setRtr} step={.01} min={0} max={1} />}
            {(noseType === 'secant' || noseType === 'haack') && <InputSlider label="Meplat" value={meplat} setValue={setMeplat} step={.001} min={0} max={diameter / 2} />}
            {noseType === 'power' && <InputSlider label="Power" value={power} setValue={setPower} step={.01} min={.5} max={.99} />}
            {noseType === 'haack' && <InputSlider label="C" value={haackC} setValue={setHaackC} step={.01} min={0} max={.66} />}
            {material !== 'jacket' && <InputSlider label="Driving Bands" value={drivingBands} setValue={setDrivingBands} step={1} min={0} max={5} />}
            <InputSlider label="Bearing Surface Length" value={bearingSurfaceLength} setValue={setBearingSurfaceLength} step={.001} min={0} max={diameter * 5} />
            <InputSlider label="Boattail Length" value={boattailLength} setValue={setBoattailLength} step={.001} min={0} max={diameter * 2.5} />
            <InputSlider label="Base Diameter" value={baseDiameter} setValue={setBaseDiameter} step={.001} min={0} max={diameter} />
            {isAdmin && material !== 'jacket' && <InputSlider label="Base Cavity Diameter" value={baseCavityDiameter} setValue={setBaseCavityDiameter} step={.001} min={0} max={baseDiameter} />}
            {isAdmin && material !== 'jacket' && <InputSlider label="Base Cavity Length" value={baseCavityLength} setValue={setBaseCavityLength} step={.001} min={0} max={diameter * 4} />}
            {isAdmin && material !== 'jacket' && <InputSlider label="Tip Length" value={tipLength} setValue={setTipLength} step={.001} min={0} max={noseLength} />}
            {isAdmin && material !== 'jacket' && <InputSlider label="Tip Stem Diameter" value={tipStemDiameter} setValue={setTipStemDiameter} step={.001} min={0} max={diameter} />}
            {isAdmin && material !== 'jacket' && <InputSlider label="Tip Stem Length" value={tipStemLength} setValue={setTipStemLength} step={.001} min={0} max={diameter * 4} />}
            {isAdmin && material !== 'jacket' && <InputSlider label="Tip Core Length" value={tipCoreLength} setValue={setTipCoreLength} step={.001} min={0} max={diameter * 4} />}
            <InputSlider label="Target Range" value={targetRange} setValue={setTargetRange} step={100} min={100} max={3000} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={9}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <canvas ref={printRef} width={1500} height={800} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography variant="h5" style={{ textAlign: 'center' }}>
                    Bullet Optimizer
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container p={2} spacing={3}>
                    <Grid item xs={12}>
                      <Button disabled={searching} variant="contained" fullWidth onClick={() => setSearching(searching => !searching)}>
                        {searching ? `Searching (${iterations})` : 'Start'}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Optimize</InputLabel>
                        <Select
                          fullWidth
                          value={searchParams.optimize}
                          label="Optimize"
                          onChange={e => setSearchParam('optimize', e.target.value)}
                        >
                          <MenuItem value="superFF">Supersonic FF</MenuItem>
                          <MenuItem value="superBC">Supersonic BC</MenuItem>
                          <MenuItem value="subFF">Subsonic FF</MenuItem>
                          <MenuItem value="subBC">Subsonic BC</MenuItem>
                          <MenuItem value="windMOA">Wind Drift</MenuItem>
                          <MenuItem value="dropMOA">Bullet Drop</MenuItem>
                          {/*<MenuItem value="jump">Dispersion (σ)</MenuItem>*/}
                          {isAdmin && material !== 'jacket' && <MenuItem value="penetration">Armor Penetration</MenuItem>}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth label="Iterations" value={searchIterations} onChange={e => setSearchIterations(e.target.value)} />
                    </Grid>
                    {[ 'windMOA', 'dropMOA', 'jump', 'penetration' ].includes(searchParams.optimize) && <>
                      <Grid item xs={6}>
                        <TextField fullWidth label="Target Range" value={searchParams.targetRange} onChange={e => setSearchParam('targetRange', e.target.value)} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth label={searchParams.useBaseWeight ? "Known Velocity" : "Velocity"} value={searchParams.velocity} onChange={e => setSearchParam('velocity', e.target.value)} />
                      </Grid>
                      <Grid item xs={searchParams.useBaseWeight ? 6 : 12}>
                        <FormGroup>
                          <FormControlLabel control={
                            <Checkbox checked={searchParams.useBaseWeight} onChange={e => setSearchParam('useBaseWeight', !searchParams.useBaseWeight)} />
                            } label="Adjust velocity for weight"  />
                        </FormGroup>
                      </Grid>
                      {searchParams.useBaseWeight && <Grid item xs={6}>
                        <TextField fullWidth label="Known Weight" value={searchParams.baseWeight} onChange={e => setSearchParam('baseWeight', e.target.value)} />
                      </Grid>}
                    </>}
                    <Grid item xs={12}>
                      <Typography variant="h5" gutterBottom style={{ textAlign: 'center' }}>
                        Dimensions
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <DoubleInputSlider
                        label="Caliber (inches)" min={1} max={16} step={1}
                        value={[ searchParams.caliberId.min, searchParams.caliberId.max ]}
                        format={value => calibers[value]}
                        setValue={value => {
                          setSearchParam('caliberId', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Bearing Surface Length (calibers)" min={0} max={5} step={.01}
                        value={[ searchParams.bearingSurfaceLength.min, searchParams.bearingSurfaceLength.max ]}
                        setValue={value => {
                          setSearchParam('bearingSurfaceLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    {material === 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Jacket Thickness (calibers)" min={0.001} max={.2} step={.001}
                        value={[ searchParams.jacketThickness.min, searchParams.jacketThickness.max ]}
                        setValue={value => {
                          setSearchParam('jacketThickness', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Boattail Length (calibers)" min={0} max={2} step={.01}
                        value={[ searchParams.boattailLength.min, searchParams.boattailLength.max ]}
                        setValue={value => {
                          setSearchParam('boattailLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Base Diameter (calibers)" min={0} max={1} step={.01}
                        value={[ searchParams.baseDiameter.min, searchParams.baseDiameter.max ]}
                        setValue={value => {
                          setSearchParam('baseDiameter', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    {material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Driving Bands" min={0} max={5} step={1}
                        value={[ searchParams.drivingBands.min, searchParams.drivingBands.max ]}
                        setValue={value => {
                          setSearchParam('drivingBands', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {isAdmin && material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Base Cavity Diameter (calibers)" min={0} max={1} step={.01}
                        value={[ searchParams.baseCavityDiameter.min, searchParams.baseCavityDiameter.max ]}
                        setValue={value => {
                          setSearchParam('baseCavityDiameter', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {isAdmin && material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Base Cavity Length (calibers)" min={0} max={5} step={.01}
                        value={[ searchParams.baseCavityLength.min, searchParams.baseCavityLength.max ]}
                        setValue={value => {
                          setSearchParam('baseCavityLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {isAdmin && material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Tip Length (calibers)" min={0} max={5} step={.01}
                        value={[ searchParams.tipLength.min, searchParams.tipLength.max ]}
                        setValue={value => {
                          setSearchParam('tipLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {isAdmin && material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Tip Stem Diameter (calibers)" min={0} max={1} step={.01}
                        value={[ searchParams.tipStemDiameter.min, searchParams.tipStemDiameter.max ]}
                        setValue={value => {
                          setSearchParam('tipStemDiameter', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {isAdmin && material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Tip Stem Length (calibers)" min={0} max={5} step={.01}
                        value={[ searchParams.tipStemLength.min, searchParams.tipStemLength.max ]}
                        setValue={value => {
                          setSearchParam('tipStemLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {isAdmin && material !== 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Tip Core Length (calibers)" min={0} max={3} step={.01}
                        value={[ searchParams.tipCoreLength.min, searchParams.tipCoreLength.max ]}
                        setValue={value => {
                          setSearchParam('tipCoreLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    <Grid item xs={12}>
                      <Typography variant="h5" gutterBottom style={{ textAlign: 'center' }}>
                        Nose Dimensions
                      </Typography>
                    </Grid>
                    {material !== 'jacket' && <Grid item xs={12}>
                      <InputLabel>Nose Types</InputLabel>
                      <Grid container>
                        <Grid item xs={4}>
                          <Checkbox checked={searchParams.noseTypes.haack} onChange={e => setSearchParam('noseTypes', { ...searchParams.noseTypes, haack: e.target.checked })} />
                          <label>Haack</label>
                        </Grid>
                        <Grid item xs={4}>
                          <Checkbox checked={searchParams.noseTypes.power} onChange={e => setSearchParam('noseTypes', { ...searchParams.noseTypes, power: e.target.checked })} />
                          <label>Power</label>
                        </Grid>
                        <Grid item xs={4}>
                          <Checkbox checked={searchParams.noseTypes.secant} onChange={e => setSearchParam('noseTypes', { ...searchParams.noseTypes, secant: e.target.checked })} />
                          <label>Secant</label>
                        </Grid>
                      </Grid>
                    </Grid>}
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Nose Length (calibers)" min={.5} max={5} step={.01}
                        value={[ searchParams.noseLength.min, searchParams.noseLength.max ]}
                        setValue={value => {
                          setSearchParam('noseLength', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    {material === 'jacket' && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Nose Cavity (calibers)" min={0} max={3} step={.01}
                        value={[ searchParams.noseCavity.min, searchParams.noseCavity.max ]}
                        setValue={value => {
                          setSearchParam('noseCavity', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {(material === 'jacket' || searchParams.noseTypes.secant || searchParams.noseTypes.haack) && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Meplat (calibers)" min={0} max={.5} step={.01}
                        value={[ searchParams.meplat.min, searchParams.meplat.max ]}
                        setValue={value => {
                          setSearchParam('meplat', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {(material === 'jacket' || searchParams.noseTypes.secant) && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Rt/R" min={0} max={1} step={.01}
                        value={[ searchParams.rtr.min, searchParams.rtr.max ]}
                        setValue={value => {
                          setSearchParam('rtr', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {(material !== 'jacket' && searchParams.noseTypes.power) && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Power" min={.5} max={.99} step={.01}
                        value={[ searchParams.power.min, searchParams.power.max ]}
                        setValue={value => {
                          setSearchParam('power', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    {(material !== 'jacket' && searchParams.noseTypes.haack) && <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Haack C" min={0} max={.66} step={.01}
                        value={[ searchParams.haackC.min, searchParams.haackC.max ]}
                        setValue={value => {
                          setSearchParam('haackC', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>}
                    <Grid item xs={12}>
                      <Typography variant="h5" gutterBottom style={{ textAlign: 'center' }}>
                        Constraints
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Twist (in/rev)" min={5} max={20} step={1}
                        value={[ searchParams.twist.min, searchParams.twist.max ]}
                        setValue={value => {
                          setSearchParam('twist', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Velocity (fps)" min={0} max={6000} step={100}
                        value={[ searchParams.velocityConstraint?.min, searchParams.velocityConstraint?.max ]}
                        setValue={value => {
                          setSearchParam('velocityConstraint', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Boattail Angle (deg)" min={0} max={45} step={1}
                        value={[ searchParams.boattailAngle.min, searchParams.boattailAngle.max ]}
                        setValue={value => {
                          setSearchParam('boattailAngle', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DoubleInputSlider
                        label="Weight (grains)" min={0} max={7000} step={1}
                        value={[ searchParams.weight?.min, searchParams.weight?.max ]}
                        setValue={value => {
                          setSearchParam('weight', { min: value[0], max: value[1] })
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 485 }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>3D Model</Typography>
                {stlUrl && <>
                  <StlViewer
                    style={{ height: 350, width: '100%', border: '1px solid #ccc' }}
                    orbitControls
                    url={stlUrl}
                    shadows
                    modelProps={{
                      color: '#B87333',
                    }}
                    cameraProps={{
                      initialPosition:{
                        latitude: .5,
                        longitude: .5,
                        distance: 2
                      }
                    }}
                  />
                  <Button variant="outlined" fullWidth onClick={downloadStl} sx={{ marginTop: '5px' }}>
                    <DownloadIcon />
                    Download
                  </Button>
                </>}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 485 }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Drag Coefficients</Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dragChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="Mach" label={{ value: 'Mach', position: 'bottom', offset: -8 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="G7" dot={false} stroke="#4D5360" strokeWidth={2} />
                    <Line type="monotone" dataKey="Cd" dot={false} stroke="#FF9F40" strokeWidth={2} />
                    <Line type="monotone" dataKey="Nose" dot={false} stroke="#9966FF" strokeWidth={2} />
                    <Line type="monotone" dataKey="Surface" dot={false} stroke="#4BC0C0" strokeWidth={2} />
                    <Line type="monotone" dataKey="DrivingBand" dot={false} stroke="#FF6384" strokeWidth={2} />
                    <Line type="monotone" dataKey="Boattail" dot={false} stroke="#FFCE56" strokeWidth={2} />
                    <Line type="monotone" dataKey="Base" dot={false} stroke="#36A2EB" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>G7 BC</Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={bcChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="Mach" label={{ value: 'Mach', position: 'insideBottomLeft', offset: -10 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="BC" dot={false} stroke="#FF6384" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Gyroscopic Stability</Typography>
                {!!(mcdrag && mcdrag.SgVsTwist) && <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={mcdrag.SgVsTwist} margin={{ top: 0, right: 20, left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="twistRate" label={{ value: 'Twist Rate', position: 'bottom' }} />
                    <YAxis label={{ value: 'Sg', angle: -90, position: 'insideLeft' }} />
                    <Area name="" type="monotone" dataKey="unstable" fill="#ff0000" strokeWidth="0" opacity="0.5" />
                    <Area name="" type="monotone" dataKey="marginal" fill="#ffff00" strokeWidth="0" opacity="0.5" />
                    <Area name="" type="monotone" dataKey="stable" fill="#228b22" strokeWidth="0" opacity="0.5" />
                    <Line type="monotone" dataKey="sg" dot={false} stroke="#36A2EB" strokeWidth={2} />
                    <Tooltip
                      filterNull={true}
                      formatter={(value, name, ...args) => {
                        if (name === 'sg') return value.toFixed(2)
                        if (name === 'stable') return value[2] ? 'yes' : null
                        if (name === 'marginal') return value[2] ? 'yes' : null
                        if (name === 'unstable') return value[2] ? 'yes' : null
                        return null
                      }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>}
              </Paper>
            </Grid>
            {/* This doesn't make sense because we are manufacturing Cnpa to acheive dynamic stability
            */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Dynamic Stability</Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={sdChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="Mach" label={{ value: 'Mach', position: 'insideBottomLeft', offset: -10 }} />
                    <YAxis label={{ value: 'Sd', angle: -90, position: 'insideLeft' }} domain={[ -3, 3 ]} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Sd" dot={false} stroke="#FF6384" strokeWidth={2} />
                    <Line type="monotone" dataKey="stable" dot={false} stroke="#36A2EB" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>Aerodynamic Coefficients</Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={coeffChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="Mach" label={{ value: 'Mach', position: 'insideBottomLeft', offset: -5 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Cnpa" dot={false} stroke="#FF6384" strokeWidth={2} />
                    <Line type="monotone" dataKey="Cla" dot={false} stroke="#36A2EB" strokeWidth={2} />
                    <Line type="monotone" dataKey="Clp" dot={false} stroke="#B630D7" strokeWidth={2} />
                    <Line type="monotone" dataKey="Cma" dot={false} stroke="#FFCE56" strokeWidth={2} />
                    <Line type="monotone" dataKey="Cda2" dot={false} stroke="#4BC0C0" strokeWidth={2} />
                    <Line type="monotone" dataKey="Cna" dot={false} stroke="#9966FF" strokeWidth={2} />
                    <Line type="monotone" dataKey="Cmq" dot={false} stroke="#4D5360" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>G Code</Typography>
                <textarea readyonly value={ngc} style={{ height: '450px' }} />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  )
}

const Input = styled(MuiInput)`
  width: 70px
`

function InputSlider({ label, value, setValue, step, min, max, double }) {
  const [text, setText] = useState(value ? value.toFixed(3) : '0.000')
  const handleSliderChange = (event, newValue) => {
    setText(newValue.toFixed(3))
    setValue(newValue)
  }
  useEffect(() => {
    if (Math.abs(parseFloat(value) - parseFloat(text)) > .0001) {
      setText(value.toFixed(3))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ value ])

  const handleInputChange = event => {
    setText(event.target.value)
    setValue(event.target.value === '' ? 0 : Number(event.target.value))
  }

  const handleBlur = () => {
    if (value < min) {
      setValue(min)
    } else if (value > max) {
      setValue(max)
    }
  }

  return (
    <Box>
      <Typography id="input-slider" gutterBottom>
        {label}
      </Typography>
      <Grid container spacing={2} alignItems="center" xs={{ margin: 0 }}>
        <Grid item xs>
          <Slider
            value={typeof value === 'number' ? value : 0}
            onChange={handleSliderChange}
            max={max}
            min={min}
            step={step}
          />
        </Grid>
        <Grid item>
          <Input
            value={text}
            size="small"
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputProps={{
              step,
              min,
              max
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

function DoubleInputSlider({ label, value, setValue, step, min, max, format }) {
  const [text1, setText1] = useState(value && value[0] ? value[0].toFixed(3) : '0.000')
  const [text2, setText2] = useState(value && value[1] ? value[1].toFixed(3) : '0.000')

  useEffect(() => {
    if (Math.abs(parseFloat(value[0]) - parseFloat(text1)) > .0001) {
      setText1(value[0].toFixed(3))
    }
    if (Math.abs(parseFloat(value[1]) - parseFloat(text2)) > .0001) {
      setText2(value[1].toFixed(3))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ value ])

  const handleSliderChange = (event, newValue) => {
    setText1(newValue[0].toFixed(3))
    setText2(newValue[1].toFixed(3))
    setValue(newValue)
  }

  const handleInputChange1 = event => {
    setText1(event.target.value)
    setValue([
      event.target.value === '' ? 0 : Number(event.target.value),
      value[1]
    ])
  }

  const handleInputChange2 = event => {
    setText2(event.target.value)
    setValue([
      value[0],
      event.target.value === '' ? 0 : Number(event.target.value)
    ])
  }

  return (
    <Box>
      <Typography id="input-slider" gutterBottom>
        {label}
      </Typography>
      <Grid container spacing={2} alignItems="center" xs={{ margin: 0 }}>
        <Grid item xs>
          <Slider
            value={value}
            onChange={handleSliderChange}
            max={max}
            min={min}
            step={step}
          />
        </Grid>
        <Grid item>
          {format ? format(value[0]) : <Input
            value={text1}
            size="small"
            onChange={handleInputChange1}
          />}
        </Grid>
        <Grid item>
          -
        </Grid>
        <Grid item>
          {format ? format(value[1]) : <Input
            value={text2}
            size="small"
            onChange={handleInputChange2}
          />}
        </Grid>
      </Grid>
    </Box>
  )
}


function useLazyEffect(effect, deps = [], wait = 300) {
  const cleanUp = useRef()
  const effectRef = useRef()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updatedEffect = useCallback(effect, deps)
  effectRef.current = updatedEffect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lazyEffect = useCallback(
    debounce(() => {
      cleanUp.current = effectRef.current?.();
    }, wait, { leading: true }),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(lazyEffect, deps)
  useEffect(() => {
    return () => {
      cleanUp.current instanceof Function && cleanUp.current()
    };
  }, []);
}

const DownloadSTLButton = ({ url }) => {
  const downloadFile = () => {
    const a = document.createElement('a')
    a.href = url
    a.download = 'bullet.stl'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return <button onClick={downloadFile}>Download STL</button>;
};

export default function WrappedBulletDesigner() {
  return (
    <AutoSaveProvider>
      <BulletDesigner />
    </AutoSaveProvider>
  )
}
